import { useEffect, useState, useRef } from "react";
import { HiExclamationTriangle, HiChevronLeft, HiChevronRight } from "react-icons/hi2"
import { Tooltip } from "components/ui"

const ValidationErrors = ({
    activeSocialNetwork,
    validationErrors
}) => {

    const [networkValidationErrors, setNetworkValidationErrors] = useState([]); 
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationDirection, setAnimationDirection] = useState('');
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        const filteredErrors = validationErrors?.filter(e => e?.network == activeSocialNetwork);
        // Remove duplicates based on error message
        const uniqueErrors = filteredErrors?.filter((v, i, a) => 
            a.findIndex(v2 => (v2.error === v.error)) === i
        ) || [];
        
        setNetworkValidationErrors(uniqueErrors);
        
        // Reset index if current index is out of bounds
        if (currentIndex >= uniqueErrors.length) {
            setCurrentIndex(0);
        }
    }, [JSON.stringify(validationErrors), activeSocialNetwork, currentIndex]);

    // Check if text is truncated
    useEffect(() => {
        if (textRef.current) {
            const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTextTruncated(isOverflowing);
        }
    }, [currentIndex, networkValidationErrors]);

    const handlePrevious = () => {
        if (isAnimating) return;
        
        setIsAnimating(true);
        setAnimationDirection('slide-left');
        
        setTimeout(() => {
            setCurrentIndex((prev) => 
                prev === 0 ? networkValidationErrors.length - 1 : prev - 1
            );
            setAnimationDirection('slide-in-right');
            
            setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection('');
            }, 200);
        }, 200);
    };

    const handleNext = () => {
        if (isAnimating) return;
        
        setIsAnimating(true);
        setAnimationDirection('slide-right');
        
        setTimeout(() => {
            setCurrentIndex((prev) => 
                prev === networkValidationErrors.length - 1 ? 0 : prev + 1
            );
            setAnimationDirection('slide-in-left');
            
            setTimeout(() => {
                setIsAnimating(false);
                setAnimationDirection('');
            }, 200);
        }, 200);
    };

    // Don't render if no errors
    if (!networkValidationErrors?.length) {
        return null;
    }

    const currentError = networkValidationErrors[currentIndex];
    const totalErrors = networkValidationErrors.length;

    return (
        <div className="flex items-center gap-3 py-1 px-2 bg-yellow-600/5 text-yellow-500 border border-yellow-200/20">
            {/* Left Arrow - only show if more than one error */}
            {totalErrors > 1 && (
                <button
                    onClick={handlePrevious}
                    disabled={isAnimating}
                    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-yellow-600/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 disabled:opacity-50"
                    aria-label="Previous error"
                >
                    <HiChevronLeft className="text-lg" />
                </button>
            )}

            {/* Error Content with Animation */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                <HiExclamationTriangle className="text-base shrink-0 !mt-[1.5px]" />
                <div className="relative flex-1 min-w-0">
                    <Tooltip 
                        title={currentError?.error} 
                        placement="top"
                    >
                        <span 
                            ref={textRef}
                            className={`block truncate transition-all duration-200 ease-in-out ${
                                animationDirection === 'slide-left' ? 'transform -translate-x-full opacity-0' :
                                animationDirection === 'slide-right' ? 'transform translate-x-full opacity-0' :
                                animationDirection === 'slide-in-left' ? 'transform translate-x-0 opacity-100' :
                                animationDirection === 'slide-in-right' ? 'transform translate-x-0 opacity-100' :
                                'transform translate-x-0 opacity-100'
                            }`}
                        >
                            {currentError?.error}
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* Counter and Right Arrow */}
            {totalErrors > 1 && (
                <>
                    {/* Counter */}
                    <div className="flex items-center gap-2 text-xs font-medium shrink-0">
                        <span className="text-yellow-600">
                            {currentIndex + 1} of {totalErrors}
                        </span>
                    </div>

                    {/* Right Arrow */}
                    <button
                        onClick={handleNext}
                        disabled={isAnimating}
                        className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-yellow-600/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 disabled:opacity-50"
                        aria-label="Next error"
                    >
                        <HiChevronRight className="text-lg" />
                    </button>
                </>
            )}
        </div>
    );
};

export default ValidationErrors;